import type { FilePartInput, Part, TextPartInput } from "@openanalyst-ai/sdk/v2/client"
import { buildAppChatSystemContext, type AppChatPageView } from "./app-chat-context"
import type { AppChatAttachment } from "./app-chat-attachments"
import type { RuntimePluginApp } from "./runtime"
import { Identifier } from "@/utils/id"

type PromptPart = TextPartInput | FilePartInput
type AppChatPromptPart = PromptPart & { id: string }

export type PluginPermissionRule = { permission: string; pattern: string; action: "allow" | "deny" | "ask" }

type SessionClient = {
  session: {
    get(input: { sessionID: string; directory: string }): Promise<unknown>
    create(input: {
      directory: string
      title: string
      parentID?: string
      metadata?: Record<string, unknown>
      permission?: PluginPermissionRule[]
    }): Promise<{ data?: { id?: string } }>
    promptAsync(input: {
      sessionID: string
      directory: string
      messageID: string
      agent?: string
      model?: { providerID: string; modelID: string }
      variant?: string
      parts: PromptPart[]
      system: string
    }): Promise<unknown>
  }
}

type SendInput = {
  client: SessionClient
  directory: string
  descriptor: RuntimePluginApp
  view?: AppChatPageView
  prompt: string
  attachments?: AppChatAttachment[]
  parts?: AppChatPromptPart[]
  messageID: string
  currentSessionID?: string
  /** The active project's main/root session — this app chat is created as its child (a real subagent). */
  parentSessionID?: string
  agent?: string
  model?: { providerID: string; modelID: string }
  variant?: string
  onSessionID?: (sessionID: string) => void | Promise<void>
}

export const appChatSessionTitleSeparator = " · "

export function appChatSessionTitlePrefix(pluginTitle: string) {
  return `App: ${pluginTitle.trim() || "Plugin App"}`
}

export function appChatSessionTitle(input: { pluginTitle: string; title?: string }) {
  const title = input.title?.replace(/\s+/g, " ").trim()
  if (!title) return appChatSessionTitlePrefix(input.pluginTitle)

  const limit = 80
  const shortTitle = title.length <= limit ? title : `${title.slice(0, limit - 3).trimEnd()}...`
  return `${appChatSessionTitlePrefix(input.pluginTitle)}${appChatSessionTitleSeparator}${shortTitle}`
}

async function existingSession(input: SendInput) {
  const id = input.currentSessionID
  if (!id) return
  return input.client.session
    .get({ sessionID: id, directory: input.directory })
    .then(() => id)
    .catch(() => undefined)
}

// A plugin subagent is HARD-scoped to its own folders inside the active project:
//   {project}/.openanalyst/plugins/<id>/  (install + skills/agents/artifacts)
//   {project}/output/<id>/                (generated outputs)
// File reads/edits outside those are denied, and it cannot spawn nested subagents.
// Bash stays visible, but app chats only auto-allow obvious app-script commands;
// other shell commands still prompt through the permission dock. Tools resolve
// paths relative to the project root, and the agent's blanket "*: allow" / "read: *"
// are stripped once a session ruleset exists
// (Permission.mergeSessionMode), so these deny rules are authoritative. The "*"
// allow keeps non-file capabilities (webfetch, MCP runtime tools, ...) working as before.
function scriptBashRules(pluginID: string): PluginPermissionRule[] {
  const roots = [
    `.openanalyst/plugins/${pluginID}/Scripts`,
    `.openanalyst/plugins/${pluginID}/scripts`,
    `output/${pluginID}/scripts`,
    `output/${pluginID}/Scripts`,
  ]
  const runners = ["bun", "node", "python", "python3", "deno"]
  return roots.flatMap((root) =>
    runners.map((runner) => ({ permission: "bash", pattern: `${runner} *${root}/*`, action: "allow" as const })),
  )
}

export function pluginSubagentPermission(pluginID: string): PluginPermissionRule[] {
  const home = `.openanalyst/plugins/${pluginID}`
  const out = `output/${pluginID}`
  return [
    { permission: "*", pattern: "*", action: "allow" },
    { permission: "read", pattern: "*", action: "deny" },
    { permission: "read", pattern: `${home}/**`, action: "allow" },
    { permission: "read", pattern: `${out}/**`, action: "allow" },
    { permission: "edit", pattern: "*", action: "deny" },
    { permission: "edit", pattern: `${home}/**`, action: "allow" },
    { permission: "edit", pattern: `${out}/**`, action: "allow" },
    { permission: "bash", pattern: "*", action: "deny" },
    ...scriptBashRules(pluginID),
    { permission: "task", pattern: "*", action: "deny" },
  ]
}

type ParentCandidate = {
  id: string
  parentID?: string
  directory?: string
  title?: string
  metadata?: Record<string, unknown>
  time?: { updated?: number; created?: number }
}

/**
 * The active project's main session to parent a plugin subagent to: the most
 * recently active ROOT session for this directory that is itself NOT a plugin
 * app chat. Returns undefined when none exists yet (the chat is then created
 * standalone and links on a later send).
 */
export function resolvePluginParentSession(sessions: ParentCandidate[], directory: string): string | undefined {
  return sessions
    .filter((s) => !s.parentID && s.directory === directory)
    .filter((s) => s.metadata?.appChat !== true && !(s.title ?? "").startsWith("App: "))
    .sort((a, b) => (b.time?.updated ?? b.time?.created ?? 0) - (a.time?.updated ?? a.time?.created ?? 0))[0]?.id
}

async function createSession(input: SendInput) {
  const pluginID = input.descriptor.id
  const created = await input.client.session.create({
    directory: input.directory,
    title: appChatSessionTitle({ pluginTitle: input.descriptor.title, title: input.prompt }),
    // Real parent↔child link to the project's main session (omitted when none yet).
    ...(input.parentSessionID ? { parentID: input.parentSessionID } : {}),
    // Identify it as a depth-1 plugin subagent (depth guard + recognizability).
    metadata: { pluginId: pluginID, appChat: true, subagentDepth: 1 },
    // Hard-scope it to the plugin's own folders.
    permission: pluginSubagentPermission(pluginID),
  })
  const id = created.data?.id
  if (!id) throw new Error("App chat session could not be created.")
  return id
}

export function appChatRequestParts(input: { prompt: string; attachments?: AppChatAttachment[] }) {
  const text = input.prompt.trim()
  return [
    ...(text ? [{ id: Identifier.ascending("part"), type: "text" as const, text }] : []),
    ...(input.attachments ?? []).map((attachment) => ({
      id: Identifier.ascending("part"),
      type: "file" as const,
      mime: attachment.mime,
      filename: attachment.filename,
      url: attachment.dataUrl,
    })),
  ]
}

export function appChatOptimisticParts(input: { parts: AppChatPromptPart[]; sessionID: string; messageID: string }) {
  return input.parts.map(
    (part): Part => ({
      ...part,
      sessionID: input.sessionID,
      messageID: input.messageID,
    }),
  )
}

export async function sendAppChatPrompt(input: SendInput) {
  const sessionID = (await existingSession(input)) ?? (await createSession(input))
  await input.onSessionID?.(sessionID)
  const parts = input.parts ?? appChatRequestParts({ prompt: input.prompt, attachments: input.attachments })
  await input.client.session.promptAsync({
    sessionID,
    directory: input.directory,
    messageID: input.messageID,
    agent: input.agent,
    model: input.model,
    variant: input.variant,
    parts,
    system: buildAppChatSystemContext({
      directory: input.directory,
      descriptor: input.descriptor,
      view: input.view,
      parentSessionID: input.parentSessionID,
    }),
  })
  return { sessionID }
}
